<app-menu-common url="hr"></app-menu-common>

@if (hrService.backendMissing()) {
	<div class="hr-backend-warn">Backend RH non déployé : données indisponibles (voir docs/backend-brief-hr.md).</div>
}

<div class="flexrow hr-header">
	<button mat-icon-button aria-label="Mois précédent" (click)="onPrevMonth()">
		<mat-icon>chevron_left</mat-icon>
	</button>
	<span class="period-label" [matTooltip]="periodRange">{{ periodLabel }}</span>
	<button mat-icon-button aria-label="Mois suivant" (click)="onNextMonth()">
		<mat-icon>chevron_right</mat-icon>
	</button>
	<button mat-stroked-button class="hr-btn" (click)="onToday()">Ce mois-ci</button>

	@if (weeksLabel) {
		<span class="weeks-chip" [class.proposed]="!weeksClosed"
			[matTooltip]="weeksClosed
				? 'Semaines comptées par cette paie — arrêté enregistré'
				: 'Proposition (règle du jeudi) : rien n\'est encore arrêté pour ce mois'">
			{{ weeksLabel }}
		</span>
	}

	<!-- D'où viennent les heures : se lit avant de comprendre le tableau. -->
	<span class="source-chip" [class.clocked]="showBadged"
		matTooltip="Se règle dans Paramètres ▸ Établissements &amp; services ▸ Règles de paie">
		<mat-icon>{{ sourceIcon }}</mat-icon>&nbsp;{{ sourceLabel }}
	</span>

	@if (fullyLocked) {
		<span class="lock-chip locked"
			[matTooltip]="'Aucune écriture n\'est plus possible sur ce mois — ' + lockScopeLabel">
			<mat-icon>lock</mat-icon>&nbsp;Période verrouillée
		</span>
	} @else if (unlockedDays > 0) {
		<span class="lock-chip open"
			[matTooltip]="'Jours encore modifiables sur ' + lockScopeLabel">{{ unlockedDays }} jour{{ unlockedDays > 1 ? 's' : '' }} à verrouiller pour la paie</span>
	}

	<!-- Le mois est une ADDITION DE SEMAINES : le récapitulatif la donne, le détail la rouvre. Les
	     heures supplémentaires naissent dans la semaine — c'est là qu'on doit pouvoir les reprendre. -->
	<span class="view-toggle">
		<button mat-stroked-button class="hr-btn" [class.on]="view === 'month'" (click)="view = 'month'"
			matTooltip="Une ligne par salarié : le total du mois">Mois complet</button>
		<button mat-stroked-button class="hr-btn" [class.on]="view === 'week'" (click)="view = 'week'"
			matTooltip="Le détail des semaines comptées, où les heures supplémentaires s'attribuent">Par semaine</button>
	</span>

	<span class="spacer"></span>

	<mat-form-field class="store-select">
		<mat-label>Magasin</mat-label>
		<mat-select [formControl]="storeCtrl">
			<mat-option [value]="null">Tous</mat-option>
			@for (s of stores; track s.id) {
				<mat-option [value]="s.id">{{ s.name }}</mat-option>
			}
		</mat-select>
	</mat-form-field>

	<!-- Le verrou ferme des DATES pour un établissement, ou pour toute l'organisation quand le
	     filtre est sur « Tous ». Le bouton nomme sa portée : personne ne doit avoir à la deviner. -->
	@if (isAdmin) {
		@if (fullyLocked) {
			<button mat-stroked-button class="hr-btn" (click)="onUnlockPeriod()"
				[matTooltip]="'Rouvrir ' + periodLabel + ' à l\'écriture — ' + lockScopeLabel">
				<mat-icon>lock_open</mat-icon>&nbsp;Déverrouiller
			</button>
		} @else {
			<button mat-stroked-button class="hr-btn" (click)="onLockPeriod()"
				[matTooltip]="'Fermer ' + periodLabel + ' à toute écriture — ' + lockScopeLabel">
				<mat-icon>lock</mat-icon>&nbsp;Verrouiller
			</button>
		}
	}

	<button mat-flat-button class="hr-btn" color="primary" (click)="onExportCsv()">
		<mat-icon>download</mat-icon>&nbsp;Exporter
	</button>
</div>

<div class="pagecontent">
	@if (loading) {
		<div class="hr-loading">Calcul du rapport…</div>
	}

	<!-- LES SEMAINES COMPTÉES. Les heures supplémentaires se comptent par semaine ENTIÈRE : une
	     semaine à cheval sur deux mois part sur l'une des deux paies, jamais les deux, jamais
	     aucune. Le logiciel propose (règle du jeudi), l'utilisateur arrête, et le choix est
	     enregistré — c'est ce qui permet au mois suivant de savoir où reprendre. -->
	@if (!loading && weekChoices.length > 0) {
		<div class="weeks-banner" [class.pending]="!weeksClosed" [class.alert]="forgottenWeeks.length > 0">
			<!-- Titre et semaines sur UNE seule ligne tant que la largeur le permet ; les puces
			     passent au-dessous d'un bloc seulement quand elles ne tiennent plus à côté. -->
			<div class="weeks-line">
				<div class="weeks-head">
					<mat-icon>date_range</mat-icon>
					<span class="weeks-title">Semaines comptées dans cette paie</span>
					@if (weeksClosed) {
						<span class="pill closed">arrêté enregistré</span>
					} @else {
						<span class="pill proposed">proposition — rien n'est arrêté</span>
					}
				</div>

				<div class="weeks-chips">
					@for (w of weekChoices; track w.monday) {
						<button type="button" class="week-chip"
							[class.on]="w.included" [class.taken]="!!w.taken_by"
							[class.carried]="w.origin === 'carried'" [class.ahead]="w.origin === 'ahead'"
							[disabled]="!isAdmin || !!w.taken_by || savingWeeks"
							[matTooltip]="weekTooltip(w)" (click)="onToggleWeek(w)">
							<mat-icon>{{ w.taken_by ? 'lock' : (w.included ? 'check_box' : 'check_box_outline_blank') }}</mat-icon>
							S{{ w.iso_week }}
							<span class="week-dates">{{ w.label.slice(w.label.indexOf('(')) }}</span>
						</button>
					}
				</div>

				@if (isAdmin && !weeksClosed) {
					<button mat-flat-button color="primary" class="weeks-action"
						[disabled]="savingWeeks" (click)="onCloseWeeks()">
						Arrêter sur ces semaines
					</button>
				}
			</div>

			@if (forgottenWeeks.length > 0) {
				<div class="weeks-alert">
					<mat-icon>warning</mat-icon>
					@for (w of forgottenWeeks; track w.monday) { <b>S{{ w.iso_week }}</b> }
					n'a jamais été comptée sur aucune paie. Cochez-la ici, ou elle restera en attente
					le mois prochain.
				</div>
			}
		</div>
	}

	<!-- L9 — les points à vérifier avant d'envoyer au comptable. Règles déterministes. -->
	@if (!loading && issues.length > 0) {
		<div class="issues-banner" [class.blocking]="issueCounts.blocking > 0">
			<div class="flexrow issues-head">
				<mat-icon>{{ issueCounts.blocking > 0 ? 'error_outline' : 'fact_check' }}</mat-icon>
				<span class="issues-title">J'ai identifié des éléments à vérifier pour préparer la paie</span>
				@if (issueCounts.blocking > 0) {
					<span class="pill blocking">{{ issueCounts.blocking }} à compléter</span>
				}
				@if (issueCounts.warning > 0) {
					<span class="pill warning">{{ issueCounts.warning }} à vérifier</span>
				}
				@if (issueCounts.info > 0) {
					<span class="pill info">{{ issueCounts.info }} information{{ issueCounts.info > 1 ? 's' : '' }}</span>
				}
				<span class="spacer"></span>
				<button mat-stroked-button (click)="issuesOpen = !issuesOpen">
					{{ issuesOpen ? 'Masquer' : 'Vérifier ensemble' }}
				</button>
			</div>

			@if (issuesOpen) {
				<ul class="issues-list">
					@for (i of issues; track $index) {
						<li class="flexrow">
							<span class="dot" [class]="i.level"></span>
							@if (i.name) { <span class="who">{{ i.name }}</span> }
							<span class="what">{{ i.detail }}</span>
							<span class="spacer"></span>
							@if (i.dat) {
								<a class="goto" [routerLink]="['/hr/planning', i.dat]">Voir sur le planning</a>
							}
							@if (i.code === 'contract-missing' && i.id_employee) {
								<a class="goto" [routerLink]="['/hr/employee', i.id_employee]">Ouvrir la fiche</a>
							}
						</li>
					}
				</ul>
			}
		</div>
	}

	@if (!loading && rows.length === 0) {
		<div class="hr-loading">Aucun salarié sur cette période.</div>
	}

	@if (!loading && rows.length > 0) {
		<div class="grid-scroll">
			<table class="report-grid">
				<thead>
					<tr class="groups">
						<th class="name-col" rowspan="2">Employé</th>
						<th [attr.colspan]="periodColspan">Heures de la période</th>
						<th [attr.colspan]="overtimeColspan">Heures supplémentaires</th>
						@if (premiumColspan > 0) {
							<th [attr.colspan]="premiumColspan">Heures particulières</th>
						}
						@if (absenceColspan > 0) {
							<th [attr.colspan]="absenceColspan">Absences</th>
						}
						@if (showVouchers) {
							<th rowspan="2" matTooltip="Le décompte détaillé, jour par jour, se lit dans l'écran Titres-restaurant">
								<a routerLink="/hr/meals">Titres-<br>restaurant</a>
							</th>
							<th rowspan="2" matTooltip="Part salariale des titres : le montant à retenir sur le bulletin">Retenue<br>titres</th>
						}
						<th rowspan="2" class="comment-col">Commentaire</th>
					</tr>
					<tr>
						<th matTooltip="Volume contractuel de la période : heures hebdomadaires × semaines comptées">Contrat</th>
						@if (showPlanned) {
							<th matTooltip="Ce que le planning prévoyait, pauses déduites — avant les journées reprises à la main">Planifié</th>
						}
						@if (showBadged) {
							<th matTooltip="Temps réellement badgé (vide sans pointeuse)">Badgé</th>
						}
						<th [matTooltip]="showBadged
							? 'Temps de travail RETENU : journée validée, sinon badgé, sinon planifié'
							: 'Temps de travail RETENU : journée clôturée à la main, sinon planning — c\'est ce volume qui part en paie'">Réalisé</th>
						@for (t of tiers; track t.kind + t.rate) {
							<th [matTooltip]="t.title" [class.editable]="isAdmin && t.kind === 'paid'">{{ t.label }}</th>
						}
						<th matTooltip="La SOMME des colonnes de majoration ci-contre, moins ce qui part en
							récupération : ce qui part au comptable. Pour payer moins, on retire des heures d'une
							colonne ou on les met en récupération — le total suit.">Heures sup<br>payées</th>
						@if (showRestEdit) {
							<th [class.editable]="isAdmin" matTooltip="Heures NON payées, mises en récupération.
								Le compteur du salarié est crédité majoration comprise : 4 h à +25 % font 5 h de repos.">
								À récupérer
							</th>
						}
						@if (showContingent) {
							<th matTooltip="Heures supplémentaires imputées sur le contingent ANNUEL (220 h à défaut d'accord).
								Au-delà, l'entreprise doit une contrepartie obligatoire en repos, qui s'ajoute au paiement.">Contingent</th>
						}
						@for (p of premiumCols; track p.code + p.rate + p.rest_rate) {
							<th [matTooltip]="p.title">{{ p.label }}</th>
						}
						@for (t of usedAbsenceTypes; track t.id) {
							<th [style.color]="t.data?.color" [matTooltip]="absenceTypeTooltip(t)">{{ t.name }}<br><small>jours</small></th>
						}
						@if (usedAbsenceTypes.length > 0) { <th>Total<br><small>jours</small></th> }
						@if (showRetenue) {
							<th matTooltip="Heures d'absence NON rémunérées : c'est la retenue à opérer sur le bulletin
								(congé sans solde, grève, mise à pied, absence injustifiée).">À retenir<br><small>heures</small></th>
						}
					</tr>
				</thead>
				<tbody>
					@for (row of rows; track row.id_employee) {
						<tr>
							<td class="name-col">
								{{ row.name }}
								@if (row.incomplete.length > 0) {
									<mat-icon class="incomplete" [matTooltip]="incompleteTooltip(row)">error_outline</mat-icon>
								}
								@if (row.issues.length > 0) {
									<mat-icon class="incomplete" [matTooltip]="legalTooltip(row)">gavel</mat-icon>
								}
								@if (showAssimilated && row.assimilated_min > 0) {
									<span class="delta" [matTooltip]="'Dont ' + hours(row.assimilated_min)
										+ ' de congés payés comptés dans l\'assiette des heures supplémentaires (Cass. soc. 10 sept. 2025)'">CP</span>
								}
							</td>
						<td class="num muted contract-cell">
								@if (contractDetail(row)) {
									<span class="factors">{{ contractDetail(row) }} =</span>
								}
								{{ hours(row.contract_min) }}
							</td>
							@if (showPlanned) {
								<td class="num muted">{{ hours(row.planned_min) }}</td>
							}
							@if (showBadged) {
								<td class="num muted">{{ row.worked_min ? hours(row.worked_min) : '—' }}</td>
							}
							<!-- LE RÉALISÉ : le temps de travail retenu, celui qui part en paie. L'écart au
							     contrat se lit ici, là où on le cherche. -->
							<td class="num strong">
								{{ hours(row.paid_min) }}
								@if (deltaLabel(row)) {
									<span class="delta" [class]="deltaClass(row)">{{ deltaLabel(row) }}</span>
								}
							</td>
							@for (t of tiers; track t.kind + t.rate) {
								@if (isAdmin && t.kind === 'paid') {
									<td class="num tier-cell">
										<span class="tier-edit">
											<input type="number" min="0" step="0.25" [formControl]="tierCtrl(row, t)"
												[attr.aria-label]="t.label + ' — ' + row.name"
												(keyup.enter)="onSaveTier(row, t)">
											<button mat-icon-button type="button" [disabled]="savingWeeks"
												[matTooltip]="'Enregistrer ' + t.label + ' pour ' + row.name"
												[attr.aria-label]="'Enregistrer ' + t.label + ' pour ' + row.name"
												(click)="onSaveTier(row, t)">
												<mat-icon>check</mat-icon>
											</button>
										</span>
									</td>
								} @else {
									<td class="num" [class.zero]="tierMinutes(row, t) === 0">
										{{ tierMinutes(row, t) ? hours(tierMinutes(row, t)) : '—' }}
									</td>
								}
							}
							<!-- Le total est CALCULÉ : il ne peut plus contredire les colonnes qui le composent. -->
							<td class="num strong payable-cell">
								{{ payableMinutes(row) ? hours(payableMinutes(row)) : '—' }}
								@if (isArbitrated(row)) {
									<button mat-icon-button type="button" class="reset-arbitration" [disabled]="savingWeeks"
										matTooltip="Répartition reprise à la main — revenir au calcul automatique"
										aria-label="Revenir au calcul automatique"
										(click)="onResetTiers(row)">
										<mat-icon>restart_alt</mat-icon>
									</button>
								}
							</td>
							<!-- METTRE DES HEURES EN RÉCUPÉRATION. On saisit ce qu'on ne paie PAS ; le
							     compteur du salarié est crédité majoration comprise, et « Heures sup
							     payées » se réduit d'autant. -->
							@if (showRestEdit) {
								@if (isAdmin) {
									<td class="num tier-cell">
										<span class="tier-edit">
											<input type="number" min="0" step="0.25" [formControl]="restCtrl(row)"
												[attr.aria-label]="'Heures à récupérer — ' + row.name"
												(keyup.enter)="onSaveRest(row)">
											<button mat-icon-button type="button" [disabled]="savingWeeks"
												[matTooltip]="'Mettre ces heures en récupération pour ' + row.name"
												[attr.aria-label]="'Mettre ces heures en récupération pour ' + row.name"
												(click)="onSaveRest(row)">
												<mat-icon>check</mat-icon>
											</button>
										</span>
										@if (restCreditedMinutes(row) > 0) {
											<span class="delta rest-credit" [matTooltip]="restTooltip(row)">
												= {{ hours(restCreditedMinutes(row)) }} de repos
											</span>
										}
									</td>
								} @else {
									<td class="num" [class.zero]="!row.rest_total_min" [matTooltip]="restTooltip(row)">
										{{ row.rest_total_min ? hours(row.rest_total_min) : '—' }}
									</td>
								}
							}
							@if (showContingent) {
								<td class="num" [class.zero]="!row.contingent.used_min"
									[matTooltip]="'Reste ' + hours(row.contingent.remaining_min) + ' sur le contingent annuel'">
									{{ row.contingent.used_min ? hours(row.contingent.used_min) : '—' }}
									@if (row.contingent.cor_balance_min > 0) {
										<mat-icon class="incomplete"
											[matTooltip]="hours(row.contingent.over_min) + ' au-delà du contingent ⇒ '
												+ hours(row.contingent.cor_balance_min) + ' de contrepartie obligatoire en repos '
												+ 'à prendre sous 2 mois (L3121-30). Elle s\'ajoute au paiement des heures.'">warning</mat-icon>
									}
								</td>
							}
							@for (p of premiumCols; track p.code + p.rate + p.rest_rate) {
								<td class="num" [class.zero]="!premiumMinutes(row, p)"
									[matTooltip]="premiumRest(row, p) > 0
										? hours(premiumRest(row, p)) + ' de récupération acquis sur ces heures'
										: ''">
									{{ premiumMinutes(row, p) ? hours(premiumMinutes(row, p)) : '—' }}
									@if (premiumRest(row, p) > 0) {
										<span class="delta">+{{ hours(premiumRest(row, p)) }} récup.</span>
									}
								</td>
							}
							@for (t of usedAbsenceTypes; track t.id) {
								<td class="num" [class.zero]="!absenceDaysOf(row, t.id!)">
									{{ absenceDaysOf(row, t.id!) || '—' }}
								</td>
							}
							@if (usedAbsenceTypes.length > 0) {
								<td class="num strong" [class.zero]="!row.absence_days">{{ row.absence_days || '—' }}</td>
							}
							@if (showRetenue) {
								<td class="num retenue" [class.zero]="!row.absence_summary.unpaid_min">
									{{ row.absence_summary.unpaid_min ? hours(row.absence_summary.unpaid_min) : '—' }}
								</td>
							}
							@if (showVouchers) {
							<td class="num" [class.zero]="!row.meal_vouchers">{{ row.meal_vouchers || '—' }}</td>
							<td class="num" [class.zero]="!row.meal_vouchers">{{ row.meal_vouchers ? euros(row.meal_employee_amount) : '—' }}</td>
						}
							<td class="comment-col">{{ row.comment }}</td>
						</tr>

						<!-- LE DÉTAIL PAR SEMAINE. Les heures supplémentaires naissent dans la semaine :
						     c'est la maille où elles s'attribuent, et le mois n'en est que la somme. -->
						@if (view === 'week') {
							@for (w of weekLinesOf(row); track w.monday) {
								<tr class="week-row">
									<td class="name-col week-name">
										<mat-icon>subdirectory_arrow_right</mat-icon>
										{{ weekLabelOf(w.monday) }}
										@if (isPartialWeek(w.monday)) {
											<mat-icon class="incomplete" [matTooltip]="partialWeekTooltip(w.monday)">info_outline</mat-icon>
										}
									</td>
									<td class="num muted">{{ hours(w.contract_min) }}</td>
									@if (showPlanned) { <td></td> }
									@if (showBadged) { <td></td> }
									<td class="num">{{ hours(w.worked_min) }}</td>
									@for (t of tiers; track t.kind + t.rate) {
										@if (isAdmin && t.kind === 'paid') {
											<td class="num tier-cell">
												<span class="tier-edit">
													<input type="number" min="0" step="0.25" [formControl]="tierCtrl(row, t, w.monday)"
														[attr.aria-label]="t.label + ' — ' + row.name + ' — semaine du ' + w.monday"
														(keyup.enter)="onSaveTier(row, t, w.monday)">
													<button mat-icon-button type="button" [disabled]="savingWeeks"
														[matTooltip]="'Enregistrer ' + t.label + ' pour la semaine du ' + w.monday"
														[attr.aria-label]="'Enregistrer ' + t.label + ' pour la semaine du ' + w.monday"
														(click)="onSaveTier(row, t, w.monday)">
														<mat-icon>check</mat-icon>
													</button>
												</span>
											</td>
										} @else {
											<td class="num" [class.zero]="tierMinutes(row, t, w.monday) === 0">
												{{ tierMinutes(row, t, w.monday) ? hours(tierMinutes(row, t, w.monday)) : '—' }}
											</td>
										}
									}
									<td class="num strong">
										{{ payableMinutes(row, w.monday) ? hours(payableMinutes(row, w.monday)) : '—' }}
									</td>
									@if (showRestEdit) {
										@if (isAdmin) {
											<td class="num tier-cell">
												<span class="tier-edit">
													<input type="number" min="0" step="0.25" [formControl]="restCtrl(row, w.monday)"
														[attr.aria-label]="'Heures à récupérer — ' + row.name + ' — semaine du ' + w.monday"
														(keyup.enter)="onSaveRest(row, w.monday)">
													<button mat-icon-button type="button" [disabled]="savingWeeks"
														[matTooltip]="'Mettre ces heures en récupération pour la semaine du ' + w.monday"
														[attr.aria-label]="'Mettre ces heures en récupération pour la semaine du ' + w.monday"
														(click)="onSaveRest(row, w.monday)">
														<mat-icon>check</mat-icon>
													</button>
												</span>
											</td>
										} @else {
											<td class="num" [class.zero]="!restTotalMinutes(row, w.monday)">
												{{ restTotalMinutes(row, w.monday) ? hours(restTotalMinutes(row, w.monday)) : '—' }}
											</td>
										}
									}
									<td [attr.colspan]="weekTailColspan - (showRestEdit ? 1 : 0)"></td>
								</tr>
							}
						}
					}
				</tbody>
				<tfoot>
					<tr>
						<td class="name-col">Total ({{ rows.length }})</td>
						<!-- Le total des heures retenues se pose SOUS la colonne qui les porte : « Payé »
						     quand elle existe, « Planifié » quand elle a été repliée. -->
						<td [attr.colspan]="periodColspan - 1"></td>
						<td class="num strong">{{ hours(totalPaid) }}</td>
						<td [attr.colspan]="tiers.length" class="num strong">{{ totalOvertime ? hours(totalOvertime) : '—' }}</td>
						<td class="num strong">{{ totalRetained ? hours(totalRetained) : '—' }}</td>
						@if (showRestEdit) {
							<td class="num strong"
								matTooltip="Total du repos acquis sur la période, majoration comprise">{{ totalRest ? hours(totalRest) : '—' }}</td>
						}
						@if (showContingent) { <td></td> }
						@if (premiumColspan > 0) {
							<td [attr.colspan]="premiumColspan"></td>
						}
						@if (usedAbsenceTypes.length > 0) {
							<td [attr.colspan]="usedAbsenceTypes.length"></td>
							<td class="num strong">{{ totalAbsenceDays || '—' }}</td>
						}
						@if (showRetenue) {
							<td class="num strong retenue">{{ totalRetenue ? hours(totalRetenue) : '—' }}</td>
						}
						@if (showVouchers) {
							<td class="num strong">{{ totalVouchers || '—' }}</td>
							<td class="num strong">{{ totalVouchers ? euros(totalVoucherRetenue) : '—' }}</td>
						}
						<td></td>
					</tr>
				</tfoot>
			</table>
		</div>

		<!-- Ce que la paie doit faire de chaque absence. Le tableau du haut compte les jours ;
		     celui-ci dit qui paie, ce que l'heure vaut, et ce que le salarié acquiert. -->
		@if (absenceRows.length > 0) {
			<h3 class="section-title">Absences de la période — traitement en paie</h3>

			@if (unqualifiedTypes.length > 0) {
				<div class="absence-warn">
					<mat-icon>help_outline</mat-icon>
					<span>
						Type{{ unqualifiedTypes.length > 1 ? 's' : '' }} d'absence non qualifié{{ unqualifiedTypes.length > 1 ? 's' : '' }} :
						<b>{{ unqualifiedTypes.join(', ') }}</b>. Leur traitement ci-dessous est une hypothèse prudente
						(rémunéré, hors assiette des heures supplémentaires). Précisez leur régime dans
						Paramètres ▸ Établissements &amp; services ▸ Types d'absence.
					</span>
				</div>
			}

			<div class="grid-scroll">
				<table class="report-grid absence-grid">
					<thead>
						<tr>
							<th class="name-col">Employé</th>
							<th>Absence</th>
							<th class="num">Jours</th>
							<th class="num">Heures</th>
							<th>Qui paie</th>
							<th>Temps de travail</th>
							<th>Congés payés</th>
							<th>Références</th>
						</tr>
					</thead>
					<tbody>
						@for (r of absenceRows; track $index) {
							<tr>
								<td class="name-col">{{ r.name }}</td>
								<td>
									{{ r.absence.name }}
									@if (!r.absence.recognized) {
										<mat-icon class="incomplete" matTooltip="Type non rattaché au catalogue légal">help_outline</mat-icon>
									}
								</td>
								<td class="num strong">{{ r.absence.days || '—' }}</td>
								<td class="num">{{ r.absence.minutes ? hours(r.absence.minutes) : '—' }}</td>
								<td><span class="pay-chip" [class]="payClass(r.absence)">{{ payLabel(r.absence) }}</span></td>
								<td>
									{{ workTimeLabel(r.absence) }}
									@if (r.absence.profile.overtime_basis) {
										<span class="delta" matTooltip="Ces heures comptent dans l'assiette de déclenchement
											des heures supplémentaires (Cass. soc. 10 sept. 2025)">heures sup</span>
									}
								</td>
								<td>{{ accrualLabel(r.absence) }}</td>
								<td class="legal-col">
									<span class="article">{{ r.absence.profile.article }}</span>
									@if (r.absence.profile.note) { <br><span class="note">{{ r.absence.profile.note }}</span> }
								</td>
							</tr>
						}
					</tbody>
				</table>
			</div>
		}

		<div class="rules-footer">
			@if (rules.idcc) {
				<span>Convention collective&nbsp;: <b>IDCC {{ rules.idcc }}{{ rules.idcc_label ? ' — ' + rules.idcc_label : '' }}</b></span>
			} @else {
				<span class="warn">Aucune convention collective renseignée — voir Paramètres ▸ Établissements &amp; services ▸ Règles de paie.</span>
			}
			<span>Seuil des heures supplémentaires&nbsp;: <b>{{ hours(rules.week_legal_min) }}/semaine</b></span>
			<span>Charges patronales&nbsp;: <b>{{ rules.employer_charges_rate }}&nbsp;%</b></span>
			<span>Décompte&nbsp;: <b>{{ sourceLabel }}</b></span>
		</div>
	}
</div>
